import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getIvrFlowDefinition,
  getVoiceTemplates,
  updateVoiceTemplate,
  createVoiceTemplate,
  deleteVoiceTemplate,
  getCompanyQuestions,
  upsertCompanyQuestion,
  deleteCompanyQuestion,
  getIvrCompanies,
} from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import './IvrFlowTab.css';

// ── Shape primitives ──────────────────────────────────────────────────────────

function Pill({ label, type = 'start' }) {
  return <div className={`fc-pill fc-pill-${type}`}>{label}</div>;
}

function ProcessBox({ title, text, color, badges = [], conditional, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const long = text?.length > 110;
  return (
    <div className={`fc-box fc-box-${color}${conditional ? ' fc-box-conditional' : ''}${disabled ? ' fc-box-disabled' : ''}`}>
      <div className="fc-box-header">
        <span className="fc-box-title">{title}</span>
        {conditional && <span className="fc-box-badge fc-badge-conditional">conditional</span>}
        {disabled && <span className="fc-box-badge fc-badge-disabled">disabled</span>}
        {badges.map((b) => (
          <span key={b} className="fc-box-badge">{b}</span>
        ))}
      </div>
      {text && (
        <p className="fc-box-text">
          {long && !expanded ? text.slice(0, 110) + '…' : text}
          {long && (
            <button className="fc-expand" onClick={() => setExpanded((v) => !v)}>
              {expanded ? ' less' : ' more'}
            </button>
          )}
        </p>
      )}
    </div>
  );
}

function Diamond({ label }) {
  return (
    <div className="fc-diamond-wrap">
      <div className="fc-diamond">
        <span className="fc-diamond-text">{label}</span>
      </div>
    </div>
  );
}

function Arrow({ label }) {
  return (
    <div className="fc-arrow">
      {label && <span className="fc-arrow-label">{label}</span>}
    </div>
  );
}

function BranchRow({ centerLabel, left, right }) {
  return (
    <div className="fc-branch-row">
      <div className="fc-branch fc-branch-left">
        {left ? (
          <>
            <div className="fc-branch-label fc-branch-label-left">{left.label}</div>
            <div className="fc-branch-lines-left">
              <div className="fc-branch-h" />
              <div className="fc-branch-v" />
            </div>
            {left.node}
          </>
        ) : (
          <div className="fc-branch-empty" />
        )}
      </div>
      <div className="fc-branch fc-branch-center">
        <Arrow label={centerLabel} />
      </div>
      <div className="fc-branch fc-branch-right">
        {right ? (
          <>
            <div className="fc-branch-label fc-branch-label-right">{right.label}</div>
            <div className="fc-branch-lines-right">
              <div className="fc-branch-h" />
              <div className="fc-branch-v" />
            </div>
            {right.node}
          </>
        ) : (
          <div className="fc-branch-empty" />
        )}
      </div>
    </div>
  );
}

function HangupNode({ label = 'Hangup' }) {
  return <div className="fc-terminal fc-terminal-hangup">{label}</div>;
}

function VoicemailNode({ onClick }) {
  return (
    <button className="fc-terminal fc-terminal-voicemail fc-terminal-link" onClick={onClick}>
      Voicemail
      <span className="fc-terminal-hint">view template ↓</span>
    </button>
  );
}

function GoodbyeNode({ label = 'Goodbye' }) {
  return <div className="fc-terminal fc-terminal-goodbye">{label}</div>;
}

function RetryNode() {
  return <div className="fc-terminal fc-terminal-retry">Re-prompt</div>;
}

function LoopNode({ label }) {
  return <div className="fc-terminal fc-terminal-retry">{label}</div>;
}

function TransferNode({ label = 'Agent' }) {
  return <div className="fc-terminal fc-terminal-transfer">{label}</div>;
}

// ── Step color mapping ───────────────────────────────────────────────────────

const STEP_COLORS = {
  greeting: 'blue',
  language: 'blue',
  'verify-dob': 'purple',
  consent: 'green',
  'no-consent': 'amber',
  'supply-items': 'blue',
  'confirm-supplies': 'blue',
  'supply-period': 'purple',
  'optional-questions': 'purple',
  disclaimer: 'amber',
  'disclaimer-cancel': 'amber',
  'order-created': 'green',
  voicemail: 'purple',
};

// ── Terminal node factory ────────────────────────────────────────────────────

function terminalFor(nextStepId, scrollToVm) {
  switch (nextStepId) {
    case 'hangup':           return <HangupNode />;
    case 'voicemail':        return <VoicemailNode onClick={scrollToVm} />;
    case 'agent-transfer':   return <TransferNode />;
    default:                 return <GoodbyeNode label="End" />;
  }
}

// ── Build badge list from step data ──────────────────────────────────────────

function buildBadges(step, config) {
  const badges = [];
  if (step.gatherDigits > 0) badges.push(`${step.gatherDigits} digit${step.gatherDigits > 1 ? 's' : ''}`);
  if (step.timeoutSeconds > 0) badges.push(`${step.timeoutSeconds}s timeout`);
  if (step.maxRetries) badges.push(`max ${step.maxRetries} tries`);
  if (step.stepType === 'loop') badges.push('loops per item');
  if (step.stepType === 'group') badges.push('section group');
  return badges;
}

// ── Optional sections panel ──────────────────────────────────────────────────

function OptionalSectionsPanel({ sections }) {
  if (!sections || sections.length === 0) return null;
  const total = sections.reduce((sum, s) => sum + s.questionCount, 0);
  return (
    <div className="fc-sections-panel">
      <div className="fc-sections-header">
        <span className="fc-sections-title">Optional Question Sections</span>
        <span className="fc-sections-count">{total} question{total !== 1 ? 's' : ''} across {sections.length} section{sections.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="fc-sections-list">
        {sections.map((s, i) => (
          <div key={s.sectionKey} className="fc-section-row">
            <span className="fc-section-order">{i + 1}</span>
            <span className="fc-section-key">{s.sectionKey}</span>
            <span className="fc-section-label">{s.label}</span>
            <span className="fc-section-qcount">{s.questionCount} Q{s.questionCount !== 1 ? 's' : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Voicemail template panel ──────────────────────────────────────────────────

const PLACEHOLDERS = [
  { token: '{CompanyName}',        desc: 'Company name' },
  { token: '{CompanyPhoneNumber}', desc: 'Callback number' },
  { token: '{CallbackCode}',       desc: 'Patient reference code' },
];

const BLANK_EDIT = { messageEnglish: '', messageSpanish: '' };
const BLANK_NEW  = { companyId: '', messageEnglish: '', messageSpanish: '' };

function TemplateRow({ template, isGlobal, onSaved, onDeactivated }) {
  const [editing, setEditing]   = useState(false);
  const [form, setForm]         = useState(BLANK_EDIT);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const openEdit = () => {
    setForm({
      messageEnglish: template?.messageEnglish ?? '',
      messageSpanish: template?.messageSpanish ?? '',
    });
    setEditing(true);
    setError('');
  };

  const save = async () => {
    if (!form.messageEnglish.trim()) { setError('English message is required.'); return; }
    try {
      setSaving(true);
      setError('');
      await updateVoiceTemplate(template.id, {
        messageEnglish: form.messageEnglish.trim(),
        messageSpanish: form.messageSpanish.trim() || null,
        isActive: true,
        displayOrder: template.displayOrder ?? 10,
      });
      setSuccess('Saved.');
      setEditing(false);
      onSaved();
    } catch (err) {
      setError('Failed to save: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    const who = isGlobal ? 'global default' : `override for "${template.companyName}"`;
    if (!window.confirm(`Deactivate ${who}?`)) return;
    try {
      await deleteVoiceTemplate(template.id);
      onDeactivated();
    } catch (err) {
      setError('Failed to deactivate: ' + parseApiError(err));
    }
  };

  const f = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  return (
    <div className={`vm-row ${!template?.isActive ? 'vm-row-inactive' : ''}`}>
      <div className="vm-row-header">
        <span className="vm-row-title">
          {isGlobal ? 'Global Default' : (template?.companyName ?? `Company ${template?.companyId}`)}
          {!template?.isActive && <span className="vm-badge-inactive">Inactive</span>}
        </span>
        <div className="vm-row-actions">
          {!editing && template && (
            <>
              <button className="vm-btn-edit" onClick={openEdit}>Edit</button>
              {!isGlobal && template.isActive && (
                <button className="vm-btn-deactivate" onClick={deactivate}>Remove</button>
              )}
            </>
          )}
        </div>
      </div>

      {success && <div className="vm-success">{success}</div>}
      {error   && <div className="vm-error">{error}</div>}

      {!editing && template && (
        <div className="vm-row-text">
          <div className="vm-lang-row">
            <span className="vm-lang">EN</span>
            <span>{template.messageEnglish}</span>
          </div>
          {template.messageSpanish && (
            <div className="vm-lang-row">
              <span className="vm-lang">ES</span>
              <span>{template.messageSpanish}</span>
            </div>
          )}
        </div>
      )}

      {!editing && !template && (
        <p className="vm-missing">No global default found. Create one below.</p>
      )}

      {editing && (
        <div className="vm-edit-form">
          <div className="vm-field">
            <label>English</label>
            <textarea
              rows={3}
              value={form.messageEnglish}
              onChange={(e) => f('messageEnglish', e.target.value)}
            />
          </div>
          <div className="vm-field">
            <label>Spanish <span className="vm-hint">(optional)</span></label>
            <textarea
              rows={2}
              value={form.messageSpanish}
              onChange={(e) => f('messageSpanish', e.target.value)}
            />
          </div>
          <div className="vm-form-actions">
            <button className="vm-btn-save" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="vm-btn-cancel" onClick={() => { setEditing(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NewOverrideForm({ onSaved, onCancel }) {
  const [form, setForm]     = useState(BLANK_NEW);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const f = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const save = async () => {
    if (!form.companyId)            { setError('Company ID is required.'); return; }
    if (!form.messageEnglish.trim()) { setError('English message is required.'); return; }
    try {
      setSaving(true);
      setError('');
      await createVoiceTemplate({
        templateKey:    'ivrResupplyVoicemail',
        companyId:      parseInt(form.companyId),
        patientTypeId:  null,
        messageEnglish: form.messageEnglish.trim(),
        messageSpanish: form.messageSpanish.trim() || null,
        isActive:       true,
        displayOrder:   10,
      });
      onSaved();
    } catch (err) {
      setError('Failed to create: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vm-new-form">
      <div className="vm-new-form-title">New Company Override</div>
      {error && <div className="vm-error">{error}</div>}
      <div className="vm-field">
        <label>Company ID</label>
        <input
          type="number"
          value={form.companyId}
          onChange={(e) => f('companyId', e.target.value)}
          placeholder="Enter company ID"
        />
      </div>
      <div className="vm-field">
        <label>English</label>
        <textarea
          rows={3}
          value={form.messageEnglish}
          onChange={(e) => f('messageEnglish', e.target.value)}
          placeholder="Message text…"
        />
      </div>
      <div className="vm-field">
        <label>Spanish <span className="vm-hint">(optional)</span></label>
        <textarea
          rows={2}
          value={form.messageSpanish}
          onChange={(e) => f('messageSpanish', e.target.value)}
        />
      </div>
      <div className="vm-form-actions">
        <button className="vm-btn-save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Create'}
        </button>
        <button className="vm-btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function VoicemailTemplatePanel({ panelRef }) {
  const [templates,   setTemplates]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [showNewForm, setShowNewForm] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = await getVoiceTemplates();
      setTemplates(all.filter((t) => t.templateKey === 'ivrResupplyVoicemail'));
    } catch (err) {
      setError('Failed to load templates: ' + parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const globalTemplate    = templates.find((t) => !t.companyId) ?? null;
  const overrides         = templates.filter((t) => !!t.companyId);
  const activeOverrides   = overrides.filter((t) => t.isActive);
  const inactiveOverrides = overrides.filter((t) => !t.isActive);

  return (
    <div className="vm-panel" ref={panelRef}>
      <div className="vm-panel-header">
        <div className="vm-panel-title">
          <span className="vm-panel-icon">&#x1F4FC;</span>
          <span>Voicemail Template</span>
          <code className="vm-panel-key">ivrResupplyVoicemail</code>
        </div>
        <p className="vm-panel-desc">
          Played when AMD detects a machine answering the call.
        </p>
        <div className="vm-placeholders">
          <span className="vm-ph-label">Placeholders:</span>
          {PLACEHOLDERS.map((p) => (
            <span key={p.token} className="vm-ph-chip" title={p.desc}>
              {p.token}
            </span>
          ))}
        </div>
      </div>

      {loading && <p className="vm-loading">Loading templates…</p>}
      {error   && <div className="vm-error">{error}</div>}

      {!loading && (
        <>
          <TemplateRow
            template={globalTemplate}
            isGlobal
            onSaved={fetchTemplates}
            onDeactivated={fetchTemplates}
          />
          {activeOverrides.map((t) => (
            <TemplateRow
              key={t.id}
              template={t}
              onSaved={fetchTemplates}
              onDeactivated={fetchTemplates}
            />
          ))}
          {inactiveOverrides.length > 0 && (
            <details className="vm-inactive-section">
              <summary>{inactiveOverrides.length} inactive override{inactiveOverrides.length > 1 ? 's' : ''}</summary>
              {inactiveOverrides.map((t) => (
                <TemplateRow
                  key={t.id}
                  template={t}
                  onSaved={fetchTemplates}
                  onDeactivated={fetchTemplates}
                />
              ))}
            </details>
          )}
          {showNewForm ? (
            <NewOverrideForm
              onSaved={() => { setShowNewForm(false); fetchTemplates(); }}
              onCancel={() => setShowNewForm(false)}
            />
          ) : (
            <button className="vm-btn-add" onClick={() => setShowNewForm(true)}>
              + Add Company Override
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Data-driven step renderer ────────────────────────────────────────────────

function isTerminal(stepId) {
  return stepId === 'hangup' || stepId === 'voicemail' || stepId === 'agent-transfer';
}

function FlowStep({ step, flow, scrollToVm }) {
  const color = STEP_COLORS[step.stepId] || 'blue';
  const badges = buildBadges(step);
  // Server resolves per-company on/off; disabled steps come back with enabled === false.
  const isDisabled = step.enabled === false;

  // Determine branching layout from step.branches
  const branches = step.branches || [];
  const forwardBranch = findForwardBranch(step, flow);
  const sideBranches = branches.filter((b) => b !== forwardBranch);

  // Steps with no meaningful branches — just box + arrow
  if (branches.length === 0) {
    return (
      <>
        <ProcessBox
          title={step.label}
          text={step.questionText}
          color={color}
          badges={badges}
          conditional={step.isConditional}
          disabled={isDisabled}
        />
        <Arrow />
      </>
    );
  }

  // AMD has unique 3-way branching
  if (step.stepId === 'greeting') {
    return (
      <>
        <ProcessBox
          title={step.label}
          text={step.questionText}
          color={color}
          badges={[...badges, 'AMD detection runs in background']}
        />
        <Arrow />
        <Diamond label="AMD Result?" />
        <BranchRow
          centerLabel="human"
          left={{ label: 'machine', node: <VoicemailNode onClick={scrollToVm} /> }}
          right={{ label: 'fax / unknown', node: <HangupNode /> }}
        />
      </>
    );
  }

  // Language selection — simple pass-through
  if (step.stepId === 'language') {
    return (
      <>
        <ProcessBox title={step.label} text={step.questionText} color={color} badges={badges} />
        <Arrow />
      </>
    );
  }

  // Supply items loop
  if (step.stepType === 'loop') {
    const noneSelected = branches.find((b) => b.condition === 'none-selected');
    return (
      <>
        <ProcessBox
          title={step.label}
          text={step.questionText}
          color={color}
          badges={badges}
          conditional={step.isConditional}
          disabled={isDisabled}
        />
        <Arrow />
        <Diamond label="Item Selection" />
        <BranchRow
          centerLabel="items accepted"
          left={{ label: 'loops', node: <LoopNode label="Next Item" /> }}
          right={noneSelected ? { label: 'none selected', node: <HangupNode /> } : undefined}
        />
      </>
    );
  }

  // Optional questions group
  if (step.stepType === 'group') {
    return (
      <>
        <ProcessBox
          title={step.label}
          text={step.questionText}
          color={color}
          badges={badges}
        />
        <Arrow />
      </>
    );
  }

  // Standard decision steps — build diamond + branch row
  const leftBranch = sideBranches.find((b) =>
    b.condition.includes('mismatch') || b.condition.includes('retry') ||
    b.condition === 'digit=2' || b.condition === 'other'
  );
  const rightBranch = sideBranches.find((b) =>
    b.condition.includes('max-attempts') || b.condition.includes('timeout') ||
    b.condition.includes('no-dob') || (b !== leftBranch && isTerminal(b.nextStepId))
  );

  let leftNode = null;
  if (leftBranch) {
    if (leftBranch.condition.includes('mismatch') || leftBranch.condition.includes('retry')) {
      leftNode = { label: leftBranch.label, node: <RetryNode /> };
    } else if (leftBranch.nextStepId === 'disclaimer') {
      leftNode = { label: leftBranch.label, node: <GoodbyeNode label="Back" /> };
    } else if (isTerminal(leftBranch.nextStepId)) {
      leftNode = { label: leftBranch.label, node: terminalFor(leftBranch.nextStepId, scrollToVm) };
    } else {
      leftNode = { label: leftBranch.label, node: <LoopNode label={leftBranch.label} /> };
    }
  }

  let rightNode = null;
  if (rightBranch) {
    rightNode = { label: rightBranch.label, node: terminalFor(rightBranch.nextStepId, scrollToVm) };
  }

  const diamondLabel = step.stepId === 'verify-dob' ? 'DOB Correct?'
    : step.stepId === 'consent' ? 'Press 1 or 2?'
    : step.stepId === 'disclaimer' ? 'Press 1 or 2?'
    : step.stepId === 'confirm-supplies' ? 'Confirm?'
    : step.stepId === 'supply-period' ? '90 or 30?'
    : step.stepId === 'no-consent' ? 'Choice?'
    : step.stepId === 'disclaimer-cancel' ? 'Sure?'
    : 'Decision';

  const centerLabel = forwardBranch ? forwardBranch.label.toLowerCase() : 'continue';

  return (
    <>
      <ProcessBox
        title={step.label}
        text={step.questionText}
        color={color}
        badges={badges}
        conditional={step.isConditional}
        disabled={isDisabled}
      />
      <Arrow />
      <Diamond label={diamondLabel} />
      <BranchRow
        centerLabel={centerLabel}
        left={leftNode}
        right={rightNode}
      />
    </>
  );
}

function findForwardBranch(step, flow) {
  const steps = flow.steps;
  const idx = steps.findIndex((s) => s.stepId === step.stepId);
  const nextStep = idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null;

  for (const b of step.branches) {
    if (nextStep && b.nextStepId === nextStep.stepId) return b;
    if (b.condition === 'digit=1' && !isTerminal(b.nextStepId)) return b;
    if (b.condition === 'correct') return b;
    if (b.condition === 'all-done' && !isTerminal(b.nextStepId)) return b;
  }
  return step.branches.find((b) => !isTerminal(b.nextStepId)) || step.branches[0];
}

// ── Per-company question customizer ──────────────────────────────────────────

function QuestionRow({ item, companyId, override, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ textEN: '', textES: '', rejectionEN: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // The server's flow-definition already reflects the override, so item.enabled is authoritative.
  const enabled = item.enabled !== false;
  const hasOverrideText = !!(override && (override.textEN || override.textES));

  const persist = async (payload) => {
    setSaving(true);
    setError('');
    try {
      await upsertCompanyQuestion(companyId, item.identifier, {
        textEN: override?.textEN ?? null,
        textES: override?.textES ?? null,
        rejectionEN: override?.rejectionEN ?? null,
        rejectionES: override?.rejectionES ?? null,
        enabled,
        ...payload,
      });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError('Failed to save: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => persist({ enabled: !enabled });

  const openEdit = () => {
    setForm({
      textEN: override?.textEN ?? '',
      textES: override?.textES ?? '',
      rejectionEN: override?.rejectionEN ?? '',
    });
    setError('');
    setEditing(true);
  };

  const save = () =>
    persist({
      textEN: form.textEN.trim() || null,
      textES: form.textES.trim() || null,
      rejectionEN: form.rejectionEN.trim() || null,
    });

  const reset = async () => {
    if (!window.confirm('Reset this question to the global default for this company?')) return;
    setSaving(true);
    setError('');
    try {
      await deleteCompanyQuestion(companyId, item.identifier);
      onChanged();
    } catch (err) {
      setError('Failed to reset: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const f = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  return (
    <div className={`qc-row${!enabled ? ' qc-row-off' : ''}`}>
      <div className="qc-row-head">
        <div className="qc-row-id">
          <code className="qc-ident">{item.identifier}</code>
          <span className="qc-section">{item.section}</span>
          {hasOverrideText && <span className="qc-badge-override">custom text</span>}
          {!item.disableable && <span className="qc-badge-required">required</span>}
          {!enabled && <span className="qc-badge-off">off</span>}
        </div>
        <div className="qc-row-actions">
          {item.disableable && (
            <label className="qc-switch" title={enabled ? 'Turn off' : 'Turn on'}>
              <input type="checkbox" checked={enabled} disabled={saving} onChange={toggle} />
              <span className="qc-switch-slider" />
            </label>
          )}
          {!editing && (
            <button className="vm-btn-edit" onClick={openEdit} disabled={saving}>Edit text</button>
          )}
          {override && (
            <button className="vm-btn-deactivate" onClick={reset} disabled={saving}>Reset</button>
          )}
        </div>
      </div>

      {error && <div className="vm-error">{error}</div>}

      {!editing && (
        <p className="qc-text">
          {item.effectiveText ? item.effectiveText : <em>(no text)</em>}
        </p>
      )}

      {editing && (
        <div className="vm-edit-form">
          <div className="vm-field">
            <label>English <span className="vm-hint">(blank = use global default)</span></label>
            <textarea rows={3} value={form.textEN} onChange={(e) => f('textEN', e.target.value)} />
          </div>
          <div className="vm-field">
            <label>Spanish <span className="vm-hint">(optional)</span></label>
            <textarea rows={2} value={form.textES} onChange={(e) => f('textES', e.target.value)} />
          </div>
          <div className="vm-form-actions">
            <button className="vm-btn-save" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="vm-btn-cancel" onClick={() => { setEditing(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionCustomizer({ flow, companyId }) {
  const [rows, setRows]       = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const refresh = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const list = await getCompanyQuestions(companyId);
      const map = {};
      list.forEach((r) => { map[r.identifier] = r; });
      setRows(map);
    } catch (err) {
      setError('Failed to load company customizations: ' + parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!companyId) {
    return (
      <div className="qc-panel qc-empty">
        Enter a Company ID above and click Load to turn questions on/off or customize their wording
        for that company.
      </div>
    );
  }

  const stepItems = (flow.steps || [])
    .filter((s) => s.identifier)
    .map((s) => ({
      identifier: s.identifier,
      section: 'Core flow',
      effectiveText: s.questionText,
      disableable: s.disableable,
      enabled: s.enabled,
    }));

  const optionalItems = (flow.optionalSections || []).flatMap((sec) =>
    (sec.questions || []).map((q) => ({
      identifier: q.identifier,
      section: sec.label,
      effectiveText: q.text,
      disableable: true,
      enabled: q.enabled,
    }))
  );

  const items = [...stepItems, ...optionalItems];

  return (
    <div className="qc-panel">
      <div className="qc-header">
        <span className="qc-title">Per-Company Questions</span>
        <span className="qc-sub">
          Turn questions on/off and override their wording for{' '}
          <strong>{flow.companyName || `company ${companyId}`}</strong>.
        </span>
      </div>

      {error && <div className="vm-error">{error}</div>}
      {loading && <p className="vm-loading">Loading…</p>}

      {items.map((item) => (
        <QuestionRow
          key={item.identifier}
          item={item}
          companyId={companyId}
          override={rows[item.identifier]}
          onChanged={refresh}
        />
      ))}
    </div>
  );
}

// ── Company selector (searchable) ────────────────────────────────────────────

const MAX_COMPANY_OPTIONS = 100;

function CompanySelector({ companies, value, onSelect, loading, companiesLoading }) {
  const [open, setOpen]           = useState(false);
  const [query, setQuery]         = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  const selected = value
    ? companies.find((c) => String(c.id) === String(value))
    : null;

  // Close the menu when clicking outside the combobox.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? companies.filter(
        (c) => c.name.toLowerCase().includes(q) || String(c.id).includes(q)
      )
    : companies;
  const filtered = matches.slice(0, MAX_COMPANY_OPTIONS);

  const choose = (c) => {
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
    onSelect(String(c.id));
  };

  const clear = () => {
    setQuery('');
    setOpen(false);
    onSelect('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault();
        choose(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const displayValue = open
    ? query
    : (selected ? `${selected.name} (#${selected.id})` : '');

  return (
    <div className="fc-company-selector">
      <label className="fc-company-label">Company</label>
      <div className="fc-company-combo" ref={wrapRef}>
        <input
          ref={inputRef}
          type="text"
          className="fc-company-input fc-company-search"
          value={displayValue}
          disabled={companiesLoading}
          placeholder={
            companiesLoading
              ? 'Loading companies…'
              : 'Search by name or ID (blank = defaults)'
          }
          onFocus={() => { setOpen(true); setHighlight(0); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls="fc-company-listbox"
          autoComplete="off"
        />
        {open && (
          <ul className="fc-company-menu" id="fc-company-listbox" role="listbox">
            {filtered.length === 0 && (
              <li className="fc-company-empty">
                {q ? `No companies match “${query}”.` : 'No companies available.'}
              </li>
            )}
            {filtered.map((c, i) => (
              <li
                key={c.id}
                role="option"
                aria-selected={String(c.id) === String(value)}
                className={
                  'fc-company-option' +
                  (i === highlight ? ' fc-company-option-active' : '') +
                  (String(c.id) === String(value) ? ' fc-company-option-selected' : '')
                }
                onMouseDown={(e) => { e.preventDefault(); choose(c); }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className="fc-company-option-name">{c.name}</span>
                <span className="fc-company-option-id">#{c.id}</span>
              </li>
            ))}
            {matches.length > filtered.length && (
              <li className="fc-company-more">
                Showing first {MAX_COMPANY_OPTIONS} of {matches.length} — keep typing to narrow.
              </li>
            )}
          </ul>
        )}
      </div>
      {loading && <span className="fc-company-loading">Loading…</span>}
      {value && (
        <button
          className="fc-company-btn fc-company-btn-clear"
          onClick={clear}
          disabled={loading}
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ── Main tab ─────────────────────────────────────────────────────────────────

export default function IvrFlowTab() {
  const [flow,      setFlow]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [companyId, setCompanyId] = useState('');

  const [companies,        setCompanies]        = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);

  const vmPanelRef = useRef(null);

  const fetchFlow = useCallback(async (cid) => {
    try {
      setLoading(true);
      setError('');
      const opts = {};
      if (cid) opts.companyId = cid;
      setFlow(await getIvrFlowDefinition(opts));
    } catch (err) {
      setError('Failed to load flow definition: ' + parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlow(); }, [fetchFlow]);

  // Load the company list once for the searchable picker.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await getIvrCompanies();
        if (active) setCompanies(list);
      } catch (err) {
        // Non-fatal: the picker just stays empty, defaults still render.
        console.error('Failed to load companies:', parseApiError(err));
      } finally {
        if (active) setCompaniesLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSelectCompany = (cid) => {
    setCompanyId(cid);
    fetchFlow(cid);
  };

  const scrollToVmPanel = () =>
    vmPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (loading && !flow) return <div className="ivr-flow-tab"><p className="fc-loading">Loading…</p></div>;
  if (error && !flow)   return <div className="ivr-flow-tab"><div className="fc-error">{error}</div></div>;

  // Separate terminal steps from flow steps
  const mainSteps = (flow?.steps || []).filter((s) => s.stepType !== 'terminal');
  const voicemailStep = (flow?.steps || []).find((s) => s.stepId === 'voicemail');

  return (
    <div className="ivr-flow-tab">
      <div className="fc-header">
        <h2>{flow.flowName}</h2>
        <p className="fc-subtitle">{flow.description}</p>
      </div>

      <CompanySelector
        companies={companies}
        value={companyId}
        onSelect={handleSelectCompany}
        loading={loading}
        companiesLoading={companiesLoading}
      />

      {flow.companyName && (
        <div className="fc-company-banner">
          Viewing flow for <strong>{flow.companyName}</strong> (ID: {flow.companyId})
        </div>
      )}

      {error && <div className="fc-error">{error}</div>}

      <div className="fc-chart">
        <Pill type="start" label="Outbound Call Initiated" />
        <Arrow />

        {mainSteps.map((step) => (
          <FlowStep key={step.stepId} step={step} flow={flow} scrollToVm={scrollToVmPanel} />
        ))}

        <Pill type="success" label="Order Created" />
      </div>

      <OptionalSectionsPanel sections={flow.optionalSections} />

      <QuestionCustomizer flow={flow} companyId={flow.companyId} />

      <VoicemailTemplatePanel panelRef={vmPanelRef} />
    </div>
  );
}
